import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePerspective } from '../contexts/PerspectiveContext';
import { logger } from '../utils/logger';

/**
 * ClientRouteWrapper
 * Wraps client routes to allow access for:
 * 1. Actual client users (userType === 'client')
 * 2. Admin users in switched client view (perspective === 'CLIENT')
 */
const ClientRouteWrapper = ({ children, currentUser, isLoading = false }) => {
  const { isInSwitchedView, perspectiveContext, isLoading: perspectiveLoading } = usePerspective();

  // Normalize user type to uppercase for comparison
  const userType = currentUser?.userType?.toUpperCase();

  // Debug logging
  logger.log('ClientRouteWrapper check:', {
    currentUser: currentUser ? { id: currentUser.id, userType: currentUser.userType } : null,
    userType,
    isLoading,
    perspectiveLoading,
    isInSwitchedView,
    currentPerspective: perspectiveContext?.currentPerspective,
    targetUserId: perspectiveContext?.targetUserId,
  });

  // Show loading state while session is being restored
  // This prevents premature redirects during page reload
  if (isLoading) {
    logger.log('ClientRouteWrapper: Session loading, showing placeholder');
    return <div style={{ padding: 16 }} />;
  }

  // If we have perspective state indicating switched view, wait for user to load
  // This handles the race condition on page reload
  if (isInSwitchedView && !currentUser) {
    logger.log('ClientRouteWrapper: Perspective exists but no user yet, showing placeholder');
    return <div style={{ padding: 16 }} />;
  }

  // If perspective is still loading and we're an internal user, wait
  // This prevents redirects while the perspective context is being verified
  if (perspectiveLoading && userType === 'INTERNAL') {
    logger.log('ClientRouteWrapper: Perspective loading for internal user, showing placeholder');
    return <div style={{ padding: 16 }} />;
  }

  // Allow if user is a client
  if (userType === 'CLIENT') {
    logger.log('ClientRouteWrapper: Allowing access - user is CLIENT');
    return children;
  }

  // Allow if admin is in switched client view
  // Check for CLIENT perspective (case-insensitive for robustness)
  const currentPerspective = perspectiveContext?.currentPerspective?.toUpperCase();
  if (
    userType === 'INTERNAL' &&
    isInSwitchedView &&
    currentPerspective === 'CLIENT'
  ) {
    logger.log('ClientRouteWrapper: Allowing access - admin in switched CLIENT view');
    return children;
  }

  // Additional check: if internal user has perspective context with target user, allow
  // This handles cases where currentPerspective might not be set correctly
  if (
    userType === 'INTERNAL' &&
    isInSwitchedView &&
    perspectiveContext?.targetUserId &&
    (perspectiveContext?.targetUserType === 'CLIENT' || perspectiveContext?.targetUserType?.toUpperCase() === 'CLIENT')
  ) {
    logger.log('ClientRouteWrapper: Allowing access - admin with CLIENT target user');
    return children;
  }

  // Otherwise redirect to appropriate location based on user type
  if (userType === 'INTERNAL') {
    logger.log('ClientRouteWrapper: Internal user without client perspective - redirecting to /org-select');
    return <Navigate to="/org-select" replace />;
  }

  logger.log('ClientRouteWrapper: Denying access - redirecting to /');
  return <Navigate to="/" replace />;
};

export default ClientRouteWrapper;
